import React, {useCallback, useState} from "react";
import {DispatchEntity} from "@ots-frontend/ots-data";
import {useLayer} from "react-laag";
import {UseLayerProps} from "react-laag/dist/useLayer";
import {GridRowContextMenu} from "../../../data-grid/DataGrid/useDataGrid";
import {EMPTY_ARRAY} from "../base/useDispatchDataGrid";

export type UseDispatchDataGridContextMenuProps = {
  dispatches?: DispatchEntity[];
  selectedDispatches?: DispatchEntity[]
}

export type UseDispatchDataGridContextMenuResult = Pick<UseLayerProps, 'layerProps' | 'renderLayer'> & {
  isOpen: boolean
  contextMenu?: GridRowContextMenu<DispatchEntity>
  onContextMenu: (dispatchId: string, event: React.MouseEvent) => void
}

export const useDispatchDataGridContextMenu = (props: UseDispatchDataGridContextMenuProps = {}): UseDispatchDataGridContextMenuResult => {
  const { dispatches = EMPTY_ARRAY, selectedDispatches = EMPTY_ARRAY } = props;

  const [contextMenu, setContextMenu] = useState<GridRowContextMenu<DispatchEntity>>();

  const _isOpen = typeof contextMenu !== "undefined";

  const {layerProps, renderLayer} = useLayer({
    isOpen: _isOpen,
    auto: true,
    placement: "bottom-end",
    triggerOffset: 16,
    containerOffset: 16,
    layerDimensions: layerSide => ({width: 200, height: contextMenu?.possibleHeight ?? 315}),
    onOutsideClick: () => setContextMenu(undefined),
    trigger: {
      getBounds: () => ({
        left: contextMenu?.bounds.x ?? 0,
        top: contextMenu?.bounds.y ?? 0,
        width: contextMenu?.bounds.width ?? 0,
        height: contextMenu?.bounds.height ?? 0,
        right: (contextMenu?.bounds.x ?? 0) + (contextMenu?.bounds.width ?? 0),
        bottom: (contextMenu?.bounds.y ?? 0) + (contextMenu?.bounds.height ?? 0)
      })
    }
  });

  const _onContextMenu = useCallback((dispatchId: string, event: React.MouseEvent) => {
    event.preventDefault()

    const dispatch = dispatches.find((d) => d.id === dispatchId)
    const hasSelections = selectedDispatches.length > 0;

    if (hasSelections) {
      setContextMenu({
        col: 0,
        row: 0,
        bounds: (event?.target as HTMLElement)?.getBoundingClientRect(),
        data: selectedDispatches,
        onItemClick: () => setContextMenu(undefined)
      })
    } else if (dispatch) {
      setContextMenu({
        col: 0,
        row: 0,
        bounds: (event?.target as HTMLElement)?.getBoundingClientRect(),
        data: [dispatch],
        onItemClick: () => setContextMenu(undefined)
      })
    } else {
      setContextMenu(undefined)
    }
  }, [dispatches, selectedDispatches])

  return {
    isOpen: _isOpen,
    layerProps: layerProps,
    renderLayer: renderLayer,
    contextMenu: contextMenu,
    onContextMenu: _onContextMenu
  };
}
